import { gql } from '@apollo/client';
import { useEffect } from 'react';

import {
  SessionIntentionNotificationCadence,
  SessionIntentionNotificationTimeOfDay,
} from '@grow-therapy-team/seedling-components';

import { Server } from '../../apollo/types';
import { useAppDependenciesContext } from '../../state';
import { SessionIntention__CreatedBy, SessionIntention__EditedBy } from './types';

export interface CurrentSessionIntention {
  shortId: string;
  contentText: string;
  contentImageUrl: string;
  notificationCadence: SessionIntentionNotificationCadence | null;
  notificationTimeOfDay: SessionIntentionNotificationTimeOfDay | null;
  createdAt: string;
  createdBy: SessionIntention__CreatedBy;
  editedBy: SessionIntention__EditedBy | null;
}

type GetCurrentIntentionQuery = {
  intentionHistory: {
    currentSessionIntention: CurrentSessionIntention;
  };
};

type GetCurrentIntentionQueryVariables = {
  clientUserShortId: string;
  providerShortId: string;
  appointmentShortId?: string;
};

export const CURRENT_INTENTION_QUERY = gql`
  query GetCurrentIntention($clientUserShortId: String!, $providerShortId: String!, $appointmentShortId: String) {
    intentionHistory(
      clientUserShortId: $clientUserShortId
      providerShortId: $providerShortId
      appointmentShortId: $appointmentShortId
    ) {
      currentSessionIntention {
        createdAt
        createdBy
        shortId
        contentText
        contentImageUrl
        notificationCadence
        notificationTimeOfDay
        editedBy
      }
    }
  }
`;

export function useGetCurrentIntentionQuery(
  clientUserShortId: string,
  providerShortId: string,
  appointmentShortId?: string,
  { skip, onCompleted }: { skip?: boolean; onCompleted?: (data: GetCurrentIntentionQuery) => void } = {},
) {
  const { useQuery } = useAppDependenciesContext();
  const { data, loading, error, refetch } = useQuery<GetCurrentIntentionQuery, GetCurrentIntentionQueryVariables>(
    CURRENT_INTENTION_QUERY,
    {
      variables: {
        clientUserShortId,
        providerShortId,
        appointmentShortId,
      },
      skip: !clientUserShortId || !providerShortId || skip,
      fetchPolicy: 'cache-and-network',
      context: {
        server: Server.MONOLITH,
      },
    },
  );

  useEffect(
    function callOnCompletedCallback() {
      if (data && onCompleted) {
        onCompleted(data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  return {
    currentIntention: data?.intentionHistory?.currentSessionIntention,
    loading,
    error,
    refetch,
  };
}
